import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceLine,
} from 'recharts';

import { useApiGateway } from 'hooks/useApi';

import { PriceTargetConsensus, PRICE_TARGET_API } from 'constants/stockApi';
import PulseLoader from 'components/Loading/Pulse';

export const PriceTarget: React.FC<
  React.PropsWithChildren<{
    ticker: string;
    currentStockPrice: number;
  }>
> = ({ ticker, currentStockPrice }) => {
  const { get: priceTargetData, isLoading, isError } = useApiGateway(PRICE_TARGET_API(ticker));

  const [priceTargets, setPriceTargets] = useState<PriceTargetConsensus[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetLow = priceTargets?.[0]?.targetLow;
  const targetHigh = priceTargets?.[0]?.targetHigh;
  const targetConsensus = priceTargets?.[0]?.targetConsensus;

  const data = [
    { name: 'Low', price: targetLow },
    { name: 'Current', price: currentStockPrice },
    { name: 'Consensus', price: targetConsensus },
    { name: 'High', price: targetHigh },
  ];

  const horizontalLineData = data.map((item) => {
    return {
      x: item?.price,
      y: 900,
    };
  });

  const dataBelow = [
    { x: targetLow, y: 900 },
    { x: Math.min(currentStockPrice, targetHigh ?? 0), y: 900 },
  ];

  const dataAbove = [
    { x: Math.min(currentStockPrice, targetHigh ?? 0), y: 900 },
    { x: targetHigh, y: 900 },
  ];

  useEffect(() => {
    (async function () {
      try {
        const resp = await priceTargetData();
        setPriceTargets(resp?.data || []);
      } catch (error) {
        setError('Failed to fetch data');
      }
    })();
  }, [ticker]);

  if (isLoading)
    return (
      <div className="text-center">
        <PulseLoader />
      </div>
    );

  if (isError) return <div>{error}</div>;

  if (!priceTargets || priceTargets.length === 0)
    return (
      <div className="flex-cal mt-4 flex grow items-center justify-center py-8 text-center text-sm text-palette-black-6">
        No data available for {ticker}
      </div>
    );

  return (
    <ResponsiveContainer width="100%" height={80}>
      <LineChart
        data={horizontalLineData}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <XAxis type="number" dataKey="x" domain={[targetLow ?? 0, targetHigh ?? 0]} hide />
        <YAxis type="number" dataKey="y" domain={[0, 2000]} hide />
        <Line
          data={dataBelow}
          type="monotone"
          dataKey="y"
          stroke="#f04662"
          dot={false}
          strokeWidth={4}
        />

        <Line
          data={dataAbove}
          type="monotone"
          dataKey="y"
          stroke="#6be594"
          dot={false}
          strokeWidth={4}
        />
        {currentStockPrice !== undefined && (
          <ReferenceLine
            x={currentStockPrice}
            stroke="#72767d"
            strokeDasharray="2 2"
            ifOverflow="extendDomain"
            strokeWidth={1.5}
            position="start"
            height={50}
          />
        )}
        {targetLow && (
          <ReferenceDot
            x={targetLow}
            y={900}
            r={6}
            fill="#f04662"
            label={{
              position: 'top',
              value: `$${targetLow}`,
              fill: '#FFF',
              fontSize: 12,
            }}
            stroke="#f04662"
          />
        )}
        {targetConsensus && (
          <ReferenceDot
            x={targetConsensus}
            y={900}
            r={6}
            fill="#303fe1"
            label={{
              position: 'top',
              value: `$${targetConsensus}`,
              fill: '#FFF',
              fontSize: 12,
              fontWeight: 600,
            }}
            stroke="#303fe1"
          />
        )}
        {targetHigh && (
          <ReferenceDot
            x={targetHigh}
            y={900}
            r={6}
            fill="#6be594"
            label={{
              position: 'top',
              value: `$${targetHigh}`,
              fill: '#FFF',
              fontSize: 12,
              fontWeight: 600,
            }}
            stroke="#6be594"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};
